import type {
    DatapointOptions,
    DatapointTimeGate,
    DatapointTimeGateAdmission,
} from 'owa-analytics-types';
import { isRunningOnWorker } from 'owa-config/lib/isRunningOnWorker';
import { trace } from 'owa-trace';

const HOUR_IN_MILLISECONDS = 3600000;
const MAX_ACTIVE_KEYS = 4096;
const VALID_KEY = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,127}$/;

interface FixedWindowPolicy {
    gateKey: string;
    windowDurationMs: number;
    maxEvents: number;
}

interface FixedWindowReservation extends FixedWindowPolicy {
    count: number;
    expiresAt: number;
}

function normalizePolicy(policy: unknown): FixedWindowPolicy | undefined {
    if (
        !policy ||
        typeof policy !== 'object' ||
        Array.isArray(policy) ||
        !('gateKey' in policy) ||
        typeof policy.gateKey !== 'string' ||
        !VALID_KEY.test(policy.gateKey)
    ) {
        return undefined;
    }
    if ('oncePer' in policy) {
        return policy.oncePer === 'calendarHour' &&
            !('windowDurationMs' in policy) &&
            !('maxEvents' in policy) &&
            Object.keys(policy).every(key => key === 'gateKey' || key === 'oncePer')
            ? {
                  gateKey: policy.gateKey,
                  windowDurationMs: HOUR_IN_MILLISECONDS,
                  maxEvents: 1,
              }
            : undefined;
    }
    if (
        !('windowDurationMs' in policy) ||
        typeof policy.windowDurationMs !== 'number' ||
        !Number.isSafeInteger(policy.windowDurationMs) ||
        policy.windowDurationMs <= 0 ||
        !('maxEvents' in policy) ||
        typeof policy.maxEvents !== 'number' ||
        !Number.isSafeInteger(policy.maxEvents) ||
        policy.maxEvents <= 0 ||
        !Object.keys(policy).every(
            key => key === 'gateKey' || key === 'windowDurationMs' || key === 'maxEvents'
        )
    ) {
        return undefined;
    }
    return {
        gateKey: policy.gateKey,
        windowDurationMs: policy.windowDurationMs,
        maxEvents: policy.maxEvents,
    };
}

/**
 * Page-lifetime authority. Keep active windows, including exhausted ones, until
 * expiry. A nondecreasing clock watermark prevents rollback reopening old windows.
 */
export class CalendarHourGate {
    private latestTime = 0;
    private nextExpiry = Infinity;
    private readonly keys = new Map<string, FixedWindowReservation>();

    reserve(policy: DatapointTimeGate, now: number): boolean {
        const normalized = normalizePolicy(policy);
        if (!normalized || !Number.isSafeInteger(now) || now < 0) {
            return false;
        }

        const { gateKey, windowDurationMs, maxEvents } = normalized;
        // Remainders avoid rounded-up quotients near Number.MAX_SAFE_INTEGER.
        const windowStart = now - (now % windowDurationMs);
        if (windowStart < this.latestTime - (this.latestTime % windowDurationMs)) {
            return false;
        }
        this.latestTime = Math.max(this.latestTime, now);
        if (this.nextExpiry <= this.latestTime) {
            this.nextExpiry = Infinity;
            for (const [key, reservation] of this.keys) {
                if (reservation.expiresAt <= this.latestTime) {
                    this.keys.delete(key);
                } else {
                    this.nextExpiry = Math.min(this.nextExpiry, reservation.expiresAt);
                }
            }
        }
        const reservation = this.keys.get(gateKey);
        if (reservation) {
            if (
                reservation.windowDurationMs !== windowDurationMs ||
                reservation.maxEvents !== maxEvents ||
                reservation.count >= maxEvents
            ) {
                return false;
            }
            reservation.count++;
            return true;
        }
        if (this.keys.size >= MAX_ACTIVE_KEYS) {
            return false;
        }
        const expiresAt =
            windowDurationMs > Number.MAX_SAFE_INTEGER - windowStart
                ? Infinity
                : windowStart + windowDurationMs;
        this.keys.set(gateKey, { ...normalized, count: 1, expiresAt });
        this.nextExpiry = Math.min(this.nextExpiry, expiresAt);
        return true;
    }
}

let pageGate: CalendarHourGate | undefined;
let workerAdmission: DatapointTimeGateAdmission | undefined;

// Future defaults belong here, shared by routing and enforcement. Currently opt-in only.
export function resolveDatapointTimeGate(options: DatapointOptions): DatapointTimeGate | undefined {
    return options.timeGate;
}

export function reserveDatapointTimeGate(policy: DatapointTimeGate): boolean {
    if (isRunningOnWorker()) {
        return false;
    }
    pageGate ??= new CalendarHourGate();
    return pageGate.reserve(policy, Date.now());
}

export function registerDatapointTimeGateAdmission(
    admission: DatapointTimeGateAdmission | undefined
): void {
    workerAdmission = admission;
}

export function runDatapointTimeGateDiagnostics(...callbacks: (() => void)[]): void {
    for (const callback of callbacks) {
        try {
            callback();
        } catch {
            try {
                trace.warn('Time-gated telemetry diagnostics failed.', 'analytics');
            } catch {
                // Never recurse into telemetry or let a failed warning prevent delivery.
            }
        }
    }
}

export async function admitDatapointTimeGate(policy: DatapointTimeGate): Promise<boolean> {
    const normalized = normalizePolicy(policy);
    if (!normalized) {
        return false;
    }
    if (!isRunningOnWorker()) {
        return reserveDatapointTimeGate(policy);
    }
    try {
        // Never retry an ambiguous RPC or authorize locally after losing the page authority.
        return (
            (await workerAdmission?.(
                'oncePer' in policy
                    ? { oncePer: 'calendarHour', gateKey: normalized.gateKey }
                    : normalized
            )) === true
        );
    } catch {
        return false;
    }
}
